import React, { useState, useEffect, useRef } from "react";
import {
  RichTextEditorComponent,
  Inject,
  Toolbar,
  HtmlEditor,
  Image,
  Link,
  Table,
  QuickToolbar,
} from "@syncfusion/ej2-react-richtexteditor";
import "./TogglableContentInput.css";

const TogglableContentInput = ({ value, onChange }) => {
  const [content, setContent] = useState(value || "");
  const editorRef = useRef(null);

  useEffect(() => {
    setContent(value || "");
  }, [value]);

  /** 🧹 Clean up pasted or edited HTML */
  const cleanHtml = (html) => {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    if (!doc.body) return "";

    // Remove all attributes except data-number
    doc.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        if (attr.name !== "data-number") el.removeAttribute(attr.name);
      });
    });

    // Format lists
    doc.querySelectorAll("li > p").forEach((p) => {
      const li = p.parentNode;
      while (p.firstChild) li.insertBefore(p.firstChild, p);
      p.remove();
    });

    // Unwrap spans in block elements
    const unwrapSpans = (element) => {
      element.querySelectorAll("span").forEach((span) => {
        const parentTag = span.parentNode?.tagName?.toLowerCase();
        if (
          ["p", "h1", "h2", "h3", "h4", "h5", "h6", "li"].includes(parentTag)
        ) {
          span.replaceWith(...span.childNodes);
        }
      });
    };
    unwrapSpans(doc.body);

    // Remove outer span wrapping everything
    if (
      doc.body.childNodes.length === 1 &&
      doc.body.firstChild.tagName === "SPAN"
    ) {
      const span = doc.body.firstChild;
      while (span.firstChild) {
        doc.body.insertBefore(span.firstChild, span);
      }
      span.remove();
    }

    // Wrap plain text with <p> tags
    doc.body.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "") {
        const span = doc.createElement("p");
        span.textContent = node.textContent;
        node.replaceWith(span);
      }
    });

    return doc.body.innerHTML.trim();
  };

  /** Handle editor changes */
  const handleEditorChange = (args) => {
    let newValue = args.value || "";
    newValue = cleanHtml(newValue);
    setContent(newValue);
    onChange(newValue);
  };

  /** Only sanitize pasted content */
  const handleBeforePasteCleanup = (args) => {
    args.value = cleanHtml(args.value);
  };

  const handlePaste = (args) => {
    args.html = cleanHtml(args.html);
  };

  /** Insert <br> button */
  const insertBreak = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const doc = editor.contentModule.getDocument();
    const selection = doc.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const br = doc.createElement("br");
    range.insertNode(br);

    const parent = br.parentNode;
    if (parent.tagName === "P" && parent.textContent === "") {
      parent.replaceWith(...parent.childNodes);
    }

    range.setStartAfter(br);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    editor.focusIn();
  };

  return (
    <div className="content-editor">
      <RichTextEditorComponent
        ref={editorRef}
        value={content}
        change={handleEditorChange}
        beforePasteCleanup={handleBeforePasteCleanup}
        paste={handlePaste}
        enterKey="BR"
        formatting={{ defaultTag: null, removeFormats: true }}
        pasteCleanup={{
          keepFormat: false,
          cleanCSS: true,
          cleanElements: true,
          cleanAttrs: [],
          prompt: false,
        }}
        toolbarSettings={{
          items: [
            "Bold",
            "Italic",
            "Underline",
            "|",
            "Formats",
            "Alignments",
            "OrderedList",
            "UnorderedList",
            "|",
            "CreateTable",
            "CreateLink",
            "Image",
            "SourceCode",
            "|",
            {
              tooltipText: "Insert Break",
              prefixIcon: "e-icons e-break",
              click: insertBreak,
              type: "Button",
            },
            "Undo",
            "Redo",
          ],
        }}
        format={{
          types: [
            { text: "Paragraph", value: "P" },
            { text: "Heading 1", value: "H1" },
            { text: "Heading 2", value: "H2" },
            { text: "Heading 3", value: "H3" },
            { text: "Heading 4", value: "H4" },
            { text: "Heading 5", value: "H5" },
            { text: "Heading 6", value: "H6" },
          ],
        }}
        height="300px"
      >
        <Inject
          services={[Toolbar, HtmlEditor, Image, Link, Table, QuickToolbar]}
        />
      </RichTextEditorComponent>
    </div>
  );
};

export default TogglableContentInput;

// // Settings for ordered list item formatting

// const handleOrderedListClick = () => {
//   const editor = editorRef.current;
//   if (!editor) return;

//   const doc = editor.contentModule.getDocument();
//   const sel = doc.getSelection();

//   if (!sel.rangeCount) return;
//   const range = sel.getRangeAt(0);
//   const node = range.startContainer;

//   // Find current paragraph or block
//   let block = node.nodeType === 1 ? node : node.parentNode;
//   while (block && !["P", "DIV", "BR"].includes(block.tagName)) {
//     block = block.parentNode;
//   }

//   // If user is on an empty new line, force a NEW <ol><li>
//   if (block && block.textContent.trim() === "") {
//     const ol = doc.createElement("ol");
//     const li = doc.createElement("li");
//     li.innerHTML = "<br>"; // so caret appears correctly
//     ol.appendChild(li);

//     block.replaceWith(ol);

//     // Move caret into <li>
//     const newRange = doc.createRange();
//     newRange.setStart(li, 0);
//     newRange.collapse(true);
//     sel.removeAllRanges();
//     sel.addRange(newRange);

//     return;
//   }

//   // Otherwise, default behavior
//   editor.executeCommand("insertOrderedList");
// };

// toolbarSettings={{
//   items: [
//     "Bold",
//     "Italic",
//     "Underline",
//     "|",
//     "Formats",
//     "Alignments",

//     // Replace this: "OrderedList",
//     {
//       tooltipText: "Ordered List",
//       prefixIcon: "e-icons e-list-ol",
//       click: handleOrderedListClick,
//       type: "Button",
//     },

//     "UnorderedList",
//     "|",
//     "CreateTable",
//     "CreateLink",
//     "Image",
//     "SourceCode",
//     "|",
//     {
//       tooltipText: "Insert Break",
//       prefixIcon: "e-icons e-break",
//       click: insertBreak,
//       type: "Button",
//     },
//     "Undo",
//     "Redo",
//   ],
// }}
